import { randomUUID } from './randomId.js';

const MAX_BUFFERED_CALLS = 200;
let sdk;
let loading;
let scheduled = false;
let denied = false;
let identity;
const pending = [];

function readIdentity(token) {
  try {
    const saved = JSON.parse(localStorage.getItem(`mp_${token}_mixpanel`) || '{}');
    if (saved.distinct_id) return saved.distinct_id;
    const key = `tasted:analytics-device:${token}`;
    let device = localStorage.getItem(key);
    if (!device) {
      device = randomUUID();
      localStorage.setItem(key, device);
    }
    return `$device:${device}`;
  } catch {
    return `$device:${randomUUID()}`;
  }
}

const sendsData = (method) => method === 'track' || method === 'alias' || method.startsWith('people.');

function invoke(method, args) {
  if (denied && sendsData(method)) return;
  if (method === 'init') {
    const [token, config, initialIdentity] = args;
    sdk.init(token, config);
    if (initialIdentity?.startsWith('$device:')) {
      sdk.register({ distinct_id: initialIdentity, $device_id: initialIdentity.slice(8) });
    }
    if (denied) sdk.opt_out_tracking();
    return;
  }
  const [first, second] = method.split('.');
  return second ? sdk[first][second](...args) : sdk[first](...args);
}

export function loadMixpanel() {
  if (sdk) return Promise.resolve();
  if (loading) return loading;
  loading = import('mixpanel-browser').then((module) => {
    sdk = module.default;
    for (const [method, args] of pending.splice(0)) {
      try { invoke(method, args); } catch { /* analytics cannot block the game */ }
    }
    return undefined;
  }).catch(() => null).finally(() => { loading = null; scheduled = false; });
  return loading;
}

function scheduleLoad() {
  if (scheduled || sdk || loading) return;
  scheduled = true;
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(() => loadMixpanel(), { timeout: 4000 });
  } else if (typeof window !== 'undefined') {
    window.setTimeout(() => loadMixpanel(), 1500);
  }
}

function call(method, args) {
  if (method === 'init') {
    identity = readIdentity(args[0]);
    args = [...args, identity];
  } else if (method === 'identify') {
    if (typeof args[0] !== 'string' || args[0].startsWith('$device:')) return -1;
    identity = args[0];
  } else if (method === 'opt_out_tracking') {
    denied = true;
    for (let i = pending.length - 1; i >= 0; i--) if (sendsData(pending[i][0])) pending.splice(i, 1);
  } else if (method === 'opt_in_tracking') {
    denied = false;
  }
  if (denied && sendsData(method)) return;
  if (sdk) return invoke(method, args);
  if (pending.length >= MAX_BUFFERED_CALLS) {
    const disposable = pending.findIndex(([name]) => sendsData(name));
    if (disposable >= 0) pending.splice(disposable, 1);
    else return;
  }
  pending.push([method, args]);
  scheduleLoad();
}

const methods = ['init', 'register', 'track', 'alias', 'identify', 'opt_in_tracking', 'opt_out_tracking'];
const deferred = Object.fromEntries(methods.map((method) => [method, (...args) => call(method, args)]));
deferred.get_distinct_id = () => sdk ? sdk.get_distinct_id() : identity;
deferred.people = Object.fromEntries(['set', 'increment'].map((method) => [method, (...args) => call(`people.${method}`, args)]));
export default deferred;
